import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Download, Upload, FileSpreadsheet, CheckCircle, XCircle, AlertCircle, Loader2, Box, Eye, Play, RotateCcw, History } from "lucide-react";
import { axiosInstance } from "../App";
import { useBox } from "../components/Layout";

export default function ImportData() {
  const { selectedBox } = useBox();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [hasOpenRegister, setHasOpenRegister] = useState(false);
  const [checkingRegister, setCheckingRegister] = useState(true);
  
  // Two-step state
  const [validationResult, setValidationResult] = useState(null);
  const [processResult, setProcessResult] = useState(null);
  
  // Import history state
  const [importHistory, setImportHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [reverting, setReverting] = useState(null);

  // Load import history
  const loadImportHistory = async () => {
    if (!selectedBox?.id) return;
    
    setLoadingHistory(true);
    try {
      const response = await axiosInstance.get(`/import/history?box_id=${selectedBox.id}`);
      setImportHistory(response.data || []);
    } catch (error) {
      console.error("Error loading import history:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Check if there's an open cash register for the selected box
  useEffect(() => {
    const checkOpenRegister = async () => {
      if (!selectedBox?.id) {
        setHasOpenRegister(false);
        setCheckingRegister(false);
        return;
      }
      
      setCheckingRegister(true);
      try {
        const response = await axiosInstance.get(`/cash-register/current?box_id=${selectedBox.id}`);
        setHasOpenRegister(response.data && response.data.status === "abierta");
      } catch (error) {
        setHasOpenRegister(false);
      } finally {
        setCheckingRegister(false);
      }
    };
    
    checkOpenRegister();
    loadImportHistory();
  }, [selectedBox]);

  const handleRevertImport = async (importId) => {
    if (!window.confirm("¿Está seguro de revertir esta importación? Esto deshará todos los cambios realizados.")) {
      return;
    }
    
    setReverting(importId);
    try {
      const response = await axiosInstance.post(`/import/${importId}/revert`);
      toast.success(response.data.message || "Importación revertida exitosamente");
      loadImportHistory();
      setProcessResult(null);
      setValidationResult(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al revertir importación");
    } finally {
      setReverting(null);
    }
  };

  const handleDownloadTemplate = async () => {
    if (!selectedBox?.id) {
      toast.error("Selecciona una caja primero");
      return;
    }
    
    try {
      const response = await axiosInstance.get(`/import/template?box_id=${selectedBox.id}`, {
        responseType: "blob"
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `plantilla_importacion_${selectedBox.name}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Plantilla descargada");
    } catch (error) {
      toast.error("Error al descargar plantilla");
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith(".xlsx") && !selectedFile.name.endsWith(".xls")) {
        toast.error("Solo se permiten archivos Excel (.xlsx, .xls)");
        return;
      }
      setFile(selectedFile);
      // Reset both states when new file is selected
      setValidationResult(null);
      setProcessResult(null);
    }
  };

  // Step 1: Validate file (no data saved)
  const handleValidate = async () => {
    if (!file) {
      toast.error("Selecciona un archivo primero");
      return;
    }
    if (!selectedBox?.id) {
      toast.error("Selecciona una caja primero");
      return;
    }
    if (!hasOpenRegister) {
      toast.error("Debes abrir la caja antes de importar datos");
      return;
    }

    setValidating(true);
    setValidationResult(null);
    setProcessResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("box_id", selectedBox.id);

      const response = await axiosInstance.post("/import/validate", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      setValidationResult(response.data);
      
      if (response.data.is_valid) {
        toast.success(`✅ ${response.data.valid} operaciones válidas. Revisa y confirma para procesar.`);
      } else if (response.data.invalid > 0) {
        toast.error(`❌ ${response.data.invalid} errores encontrados. Corrige el archivo y vuelve a validar.`);
      } else {
        toast.warning("No se encontraron operaciones para procesar");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al validar archivo");
    } finally {
      setValidating(false);
    }
  };

  // Step 2: Process file (save data)
  const handleProcess = async () => {
    if (!validationResult?.is_valid) {
      toast.error("Primero valida el archivo sin errores");
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("box_id", selectedBox.id);

      const response = await axiosInstance.post("/import/process", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      setProcessResult(response.data);
      setValidationResult(null); // Clear validation after processing
      
      if (response.data.failed === 0) {
        toast.success(`✅ ${response.data.successful} operaciones procesadas exitosamente`);
        setFile(null); // Clear file after success
      } else {
        toast.warning(`⚠️ ${response.data.successful} exitosas, ${response.data.failed} fallidas`);
      }
      
      // Reload history after successful import
      loadImportHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al procesar archivo");
    } finally {
      setLoading(false);
    }
  };

  // Reset everything
  const handleReset = () => {
    setFile(null);
    setValidationResult(null);
    setProcessResult(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Importar Datos</h1>
          <p className="text-slate-500">Carga masiva de SINPE, pagos y deudas desde Excel</p>
        </div>
        {selectedBox && (
          <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-lg border border-blue-200">
            <Box className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-700">Caja: {selectedBox.name}</span>
          </div>
        )}
      </div>

      {/* Warning if no open register */}
      {!checkingRegister && !hasOpenRegister && selectedBox && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
          <div>
            <p className="font-medium text-yellow-800">Caja no abierta</p>
            <p className="text-sm text-yellow-700">
              Debes abrir la caja <strong>{selectedBox.name}</strong> en el Dashboard antes de poder importar datos.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Download Template */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-green-600" />
              Paso 1: Descargar Plantilla
            </CardTitle>
            <CardDescription>
              Descarga la plantilla Excel con clientes y bancos de <strong>{selectedBox?.name || "la caja seleccionada"}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-slate-700">
                <p className="font-medium mb-2 text-emerald-800">💡 Modo recomendado (Cuentas por Cobrar): deje <strong>TIPO_OPERACION vacío</strong></p>
                <p className="mb-2"><strong>MONTO_COLONES</strong> se compara con DEUDA_ACTUAL:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li><strong>Positivo</strong> = el saldo final (deuda) que queda · <strong>Negativo</strong> = un abono/pago · <strong>0</strong> = queda al día</li>
                  <li>Debe 5000 y pongo 3000 → paga 2000 (entra a caja), deuda queda 3000</li>
                  <li>Debe 5000 y pongo 0 → paga todo (entran 5000), deuda 0</li>
                  <li>0 y pongo 1000 → deuda nueva 1000 (salen 1000)</li>
                  <li>Debe 10050 y pongo -4000 → abono de 4000 (entran 4000), deuda queda 6050</li>
                  <li>Debe 10050 y pongo -12000 → paga la deuda (entran 10050) y quedan 1950 a favor (salen 1950)</li>
                  <li>0 y pongo -1000 → sin deuda: queda 1000 a favor (salen 1000)</li>
                  <li className="text-emerald-700 font-medium">Deje el MONTO ya cargado igual para NO cambiar a ese cliente</li>
                </ul>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg text-sm text-slate-600">
                <p className="font-medium mb-2">(Avanzado) También puede indicar TIPO_OPERACION manualmente:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li><strong>SINPE</strong> - Pagos SINPE sin asociar a deuda</li>
                  <li><strong>PAGO_SINPE</strong> - Pagar deuda de cliente con SINPE</li>
                  <li><strong>PAGAR_DEUDA</strong> - Pagar deuda del cliente (va a Pago Días Anteriores)</li>
                  <li><strong>AUMENTO_DEUDA</strong> - Aumentar deuda de cliente existente</li>
                  <li><strong>NUEVA_DEUDA</strong> - Crear deuda para cliente nuevo</li>
                  <li><strong>NUEVA_CUENTA_PAGAR</strong> - Crear cuenta por pagar (deuda a proveedor)</li>
                  <li><strong>PAGO_CUENTA_PAGAR</strong> - Pagar cuenta por pagar existente</li>
                  <li><strong>ENTRADA</strong> - Entrada de dinero a caja</li>
                  <li><strong>SALIDA</strong> - Salida de dinero de caja</li>
                  <li><strong>ENTRADA_BANCO</strong> - Entrada a caja desde banco/bóveda</li>
                  <li><strong>SALIDA_BANCO</strong> - Salida de caja hacia banco/bóveda</li>
                </ul>
              </div>
              <Button 
                onClick={handleDownloadTemplate} 
                className="w-full gap-2"
                disabled={!selectedBox}
              >
                <Download className="w-4 h-4" />
                Descargar Plantilla Excel
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Upload File */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-600" />
              Paso 2: Cargar y Validar
            </CardTitle>
            <CardDescription>
              Sube el archivo, valida primero, y luego confirma para procesar
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <Label>Archivo Excel</Label>
                <Input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  className="mt-1.5"
                  data-testid="file-input"
                />
                {file && (
                  <p className="text-sm text-slate-500 mt-1">
                    Archivo: {file.name}
                  </p>
                )}
              </div>

              {/* Two-step buttons */}
              <div className="flex gap-2">
                <Button 
                  onClick={handleValidate} 
                  disabled={!file || !selectedBox || validating || !hasOpenRegister}
                  variant="outline"
                  className="flex-1 gap-2"
                  data-testid="validate-btn"
                >
                  {validating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Validando...
                    </>
                  ) : (
                    <>
                      <Eye className="w-4 h-4" />
                      Validar
                    </>
                  )}
                </Button>
                
                <Button 
                  onClick={handleProcess} 
                  disabled={!validationResult?.is_valid || loading}
                  className="flex-1 gap-2"
                  data-testid="process-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4" />
                      Procesar
                    </>
                  )}
                </Button>
              </div>
              
              {!hasOpenRegister && !checkingRegister && (
                <p className="text-xs text-yellow-600 text-center">
                  Abre la caja desde el Dashboard para habilitar la importación
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Validation Preview */}
      {validationResult && (
        <Card className={validationResult.is_valid ? "border-green-200" : "border-red-200"}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {validationResult.is_valid ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <XCircle className="w-5 h-5 text-red-600" />
              )}
              {validationResult.is_valid ? "Validación Exitosa - Confirma para Procesar" : "Errores Encontrados - Corrige el Archivo"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-slate-100 rounded-lg text-center">
                  <p className="text-2xl font-bold text-slate-900">{validationResult.total_rows}</p>
                  <p className="text-sm text-slate-500">Total Filas</p>
                </div>
                <div className="p-4 bg-green-100 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-700">{validationResult.valid}</p>
                  <p className="text-sm text-green-600">Válidas</p>
                </div>
                <div className="p-4 bg-red-100 rounded-lg text-center">
                  <p className="text-2xl font-bold text-red-700">{validationResult.invalid}</p>
                  <p className="text-sm text-red-600">Con Errores</p>
                </div>
              </div>

              {/* Preview of valid operations */}
              {validationResult.preview && validationResult.preview.length > 0 && (
                <div>
                  <h4 className="font-medium text-slate-700 mb-2">Vista previa de operaciones:</h4>
                  <div className="max-h-48 overflow-y-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">Fila</th>
                          <th className="px-3 py-2 text-left">Tipo</th>
                          <th className="px-3 py-2 text-left">Cliente/Banco</th>
                          <th className="px-3 py-2 text-right">Monto ₡</th>
                        </tr>
                      </thead>
                      <tbody>
                        {validationResult.preview.map((op, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="px-3 py-2">{op.row}</td>
                            <td className="px-3 py-2">
                              <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700">
                                {op.type}
                              </span>
                            </td>
                            <td className="px-3 py-2">{op.customer || op.bank || "-"}</td>
                            <td className="px-3 py-2 text-right">
                              {op.type === 'SALDO_FINAL'
                                ? <span className="text-xs text-slate-600">{op.acciones}{typeof op.target_balance === 'number' ? ` → saldo ₡${op.target_balance.toLocaleString()}` : ''}</span>
                                : `₡${op.amount_colones?.toLocaleString()}`}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Errors */}
              {validationResult.errors && validationResult.errors.length > 0 && (
                <div>
                  <h4 className="font-medium text-red-700 mb-2">Errores a corregir:</h4>
                  <div className="max-h-48 overflow-y-auto border border-red-200 rounded-lg bg-red-50">
                    {validationResult.errors.map((err, idx) => (
                      <div key={idx} className="px-3 py-2 border-b border-red-100 last:border-0 flex items-start gap-2">
                        <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                        <span className="text-sm text-red-700">
                          <strong>{err.row}:</strong> {err.error}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={handleReset} className="gap-2">
                  <XCircle className="w-4 h-4" />
                  Cancelar
                </Button>
                {validationResult.is_valid && (
                  <Button onClick={handleProcess} disabled={loading} className="gap-2 flex-1">
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Procesando...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        Confirmar y Procesar {validationResult.valid} Operaciones
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Process Results */}
      {processResult && (
        <Card className="border-green-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Importación Completada
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-slate-100 rounded-lg text-center">
                  <p className="text-2xl font-bold text-slate-900">{processResult.total_rows}</p>
                  <p className="text-sm text-slate-500">Total</p>
                </div>
                <div className="p-4 bg-green-100 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-700">{processResult.successful}</p>
                  <p className="text-sm text-green-600">Exitosas</p>
                </div>
                <div className="p-4 bg-red-100 rounded-lg text-center">
                  <p className="text-2xl font-bold text-red-700">{processResult.failed}</p>
                  <p className="text-sm text-red-600">Fallidas</p>
                </div>
              </div>

              {/* Successful Operations */}
              {processResult.operations && processResult.operations.length > 0 && (
                <div>
                  <h4 className="font-medium text-green-700 mb-2">Operaciones procesadas:</h4>
                  <div className="max-h-48 overflow-y-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">Fila</th>
                          <th className="px-3 py-2 text-left">Tipo</th>
                          <th className="px-3 py-2 text-left">Cliente</th>
                          <th className="px-3 py-2 text-right">Monto</th>
                        </tr>
                      </thead>
                      <tbody>
                        {processResult.operations.map((op, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="px-3 py-2">{op.row}</td>
                            <td className="px-3 py-2">
                              <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-700">
                                {op.type}
                              </span>
                            </td>
                            <td className="px-3 py-2">{op.customer || op.bank || "-"}</td>
                            <td className="px-3 py-2 text-right">
                              {op.type === 'SALDO_FINAL'
                                ? <span className="text-xs text-slate-600">{op.action}</span>
                                : `₡${op.amount?.toLocaleString()}`}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <Button variant="outline" onClick={handleReset} className="w-full gap-2">
                <Upload className="w-4 h-4" />
                Importar Otro Archivo
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Import History - Revert Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Historial de Importaciones
          </CardTitle>
          <CardDescription>
            Importaciones recientes que pueden ser revertidas
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingHistory ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : importHistory.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <History className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No hay importaciones registradas para esta caja</p>
            </div>
          ) : (
            <div className="space-y-3">
              {importHistory.map((imp) => (
                <div 
                  key={imp.id} 
                  className={`flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 ${imp.register_closed ? 'opacity-70 bg-slate-50' : ''}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <FileSpreadsheet className="w-4 h-4 text-green-600" />
                      <span className="font-medium">{imp.filename || "Importación"}</span>
                      {imp.register_closed && (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">Caja Cerrada</span>
                      )}
                    </div>
                    <div className="text-sm text-slate-500 mt-1">
                      <span>{new Date(imp.created_at).toLocaleString()}</span>
                      <span className="mx-2">•</span>
                      <span>Por: {imp.created_by}</span>
                      <span className="mx-2">•</span>
                      <span className="text-green-600">{imp.total_successful} operaciones</span>
                    </div>
                    {imp.register_closed && (
                      <p className="text-xs text-red-500 mt-1">Revierta el cierre de caja primero para poder revertir esta importación</p>
                    )}
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleRevertImport(imp.id)}
                    disabled={reverting === imp.id || imp.register_closed}
                    className="gap-2"
                    title={imp.register_closed ? "Caja cerrada - revierta el cierre primero" : "Revertir importación"}
                  >
                    {reverting === imp.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RotateCcw className="w-4 h-4" />
                    )}
                    Revertir
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
